import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { db } from '../lib/firebase';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import CoursedeeNavbar from '../components/CoursedeeNavbar';

// Add CSS for twinkle animation
const style = document.createElement('style');
style.textContent = `
  @keyframes twinkle {
    0%, 100% { opacity: 0.2; }
    50% { opacity: 1; }
  }
`;
document.head.appendChild(style);
import { 
  Sparkles, 
  Zap, 
  Shield, 
  Diamond, 
  Star, 
  Users, 
  Clock, 
  Award,
  ChevronRight,
  CheckCircle,
  CreditCard,
  Smartphone,
  Wallet,
  Banknote,
  Globe,
  Facebook,
  Youtube,
  Music,
  MessageCircle,
  Image,
  BookOpen,
  GraduationCap,
  ShoppingCart
} from 'lucide-react';

interface AIPackage {
  id: string;
  name: string;
  version: string;
  price: number;
  description: string;
  features: string[];
  coverImage: string;
  active: boolean;
  order: number;
}

const CoursedeeLanding: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addItem } = useCart();
  const [packages, setPackages] = useState<AIPackage[]>([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  
  const [footerSettings, setFooterSettings] = useState({
    social: {
      facebook: '',
      youtube: '',
      tiktok: ''
    },
    footerLinks: {
      privacy: '/privacy',
      terms: '/terms',
      contact: '/contact'
    }
  });

  const hostname = window.location.hostname;
  const domainName = hostname === 'edumi.co' || hostname === 'www.edumi.co' ? 'EduMi' : 'AIForYou';

  // Handle scroll to section from navigation
  useEffect(() => {
    const state = location.state as { scrollTo?: string };
    if (state?.scrollTo) {
      // Wait for DOM to be ready
      setTimeout(() => {
        const element = document.getElementById(state.scrollTo);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
      // Clear the state to prevent re-scrolling on component updates
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Load packages from Firestore
  useEffect(() => {
    const loadPackages = async () => {
      try {
        const q = query(
          collection(db, 'aiPackages'),
          where('active', '==', true)
        );
        const snapshot = await getDocs(q);
        const packagesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as AIPackage[];
        
        // Sort by order in JavaScript
        packagesData.sort((a, b) => (a.order || 0) - (b.order || 0));
        
        setPackages(packagesData);
      } catch (error) {
        console.error('Error loading packages:', error);
      } finally {
        setLoadingPackages(false);
      }
    };
    loadPackages();
  }, []);

  // Load footer settings from Firestore
  useEffect(() => {
    const loadFooterSettings = async () => {
      try {
        const docRef = doc(db, 'systemSettings', 'general');
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          setFooterSettings({
            social: {
              facebook: data.social?.facebook || '',
              youtube: data.social?.youtube || '',
              tiktok: data.social?.tiktok || ''
            },
            footerLinks: {
              privacy: data.footerLinks?.privacy || '/privacy',
              terms: data.footerLinks?.terms || '/terms',
              contact: data.footerLinks?.contact || '/contact'
            }
          });
        }
      } catch (error) {
        console.error('Error loading footer settings:', error);
      }
    };
    loadFooterSettings();
  }, []);
  
  console.log('🔍 Hostname:', hostname);
  console.log('🖼️ Hero Banner Path:', hostname === 'edumi.co' || hostname === 'www.edumi.co' ? '/hero-banner-edumi-pc.jpg' : '/www-coursedee-bg-web.jpg');

  useEffect(() => {
    document.title = `${domainName} - สร้างสรรค์ทุกไอเดีย ด้วยพลังของ AI`;
    
    // Testimonial Carousel Animation
    const carousel = document.getElementById('testimonialCarousel');
    if (carousel) {
      let currentPosition = 0;
      const totalCards = 6;
      
      const moveCarousel = () => {
        const isMobile = window.innerWidth < 768;
        const cardsPerView = isMobile ? 1 : 3;
        const maxPosition = totalCards - cardsPerView;
        
        currentPosition = (currentPosition + 1) % (maxPosition + 1);
        const translateX = currentPosition * -(100 / cardsPerView); // 100% for mobile, 33.333% for desktop
        carousel.style.transform = `translateX(${translateX}%)`;
      };
      
      // Handle window resize
      const handleResize = () => {
        currentPosition = 0; // Reset position on resize
        carousel.style.transform = 'translateX(0)';
      };
      
      window.addEventListener('resize', handleResize);
      
      // Start carousel animation
      const interval = setInterval(moveCarousel, 15000); // 15 seconds
      
      return () => {
        clearInterval(interval);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  const handlePackageSelect = (version: string) => {
    // Find package by version
    const pkg = packages.find(p => p.version === version);
    if (!pkg) return;

    const packageDetails = {
      id: pkg.id,
      name: pkg.name,
      price: pkg.price,
      version: pkg.version,
      description: pkg.description
    };
    
    // Save to localStorage for checkout
    const checkoutData = {
      plan: 'coursedee_cart',
      planName: packageDetails.name,
      planPrice: packageDetails.price,
      total: packageDetails.price,
      isNewPurchase: true,
      isCoursedeeCart: true,
      items: [packageDetails],
      source: 'coursedee'
    };
    localStorage.setItem('coursedeeCartCheckout', JSON.stringify(checkoutData));
    
    // Navigate to checkout
    navigate('/subscription/checkout-new?source=coursedee_cart');
  };

  const handleAddToCart = (packageData: { id: string; name: string; price: number; version: string; description: string }) => {
    addItem(packageData);
  };


  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Fixed Background */}
      <div 
        className="fixed inset-0 z-0 bg-black"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-purple-950/20 via-black to-blue-950/20"></div>
        {/* Stars */}
        <div className="absolute inset-0">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-white rounded-full"
              style={{
                width: Math.random() * 2 + 'px',
                height: Math.random() * 2 + 'px',
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
                opacity: Math.random() * 0.6 + 0.2,
                animation: `twinkle ${Math.random() * 3 + 2}s infinite`
              }}
            />
          ))}
        </div>
      </div>
      
      {/* Navigation Header */}
      <CoursedeeNavbar />

      {/* Hero Banner Section */}
      <section id="home" className="relative w-full overflow-hidden mt-16">
        {/* Desktop Banner */}
        <div className="hidden md:block w-full">
          <img 
            src="/hero-banner-coursedee.jpg" 
            alt="Coursedee Hero Banner" 
            className="w-full h-auto object-cover"
            loading="eager"
          />
        </div>
        
        {/* Mobile Banner */}
        <div className="block md:hidden w-full">
          <img 
            src="/hero-banner-coursedee-mobile.jpg" 
            alt="Coursedee Hero Banner Mobile" 
            className="w-full h-auto object-cover"
            loading="eager"
          />
        </div>
      </section>

      {/* Content */}
      <div className="relative z-10">
        {/* Course Selection Section */}
        <section id="courses" className="pt-20 pb-20 px-4 bg-black/40">
        <div className="container mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12">
            เลือกคอร์สที่ต้องการ
          </h2>

          {loadingPackages ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-500"></div>
            </div>
          ) : (
            <div className="flex flex-wrap justify-center gap-6">
              {packages.map((pkg) => {
                // Define gradient colors based on version
                const gradientMap: { [key: string]: { from: string; to: string; border: string; icon: string; textColor: string; badge: string } } = {
                  'V1': { 
                    from: 'from-blue-500', 
                    to: 'to-purple-500', 
                    border: 'border-blue-500/50 hover:border-blue-400',
                    icon: 'bg-blue-500/20 border-blue-500 text-blue-400',
                    textColor: 'text-blue-300',
                    badge: 'linear-gradient(135deg, #60A5FA 0%, #818CF8 50%, #A78BFA 100%)'
                  },
                  'V2': { 
                    from: 'from-orange-500', 
                    to: 'to-red-500', 
                    border: 'border-orange-500/50 hover:border-orange-400',
                    icon: 'bg-orange-500/20 border-orange-500 text-orange-400',
                    textColor: 'text-orange-300',
                    badge: 'linear-gradient(135deg, #FB923C 0%, #FCD34D 50%, #F59E0B 100%)'
                  },
                  'V3': { 
                    from: 'from-pink-500', 
                    to: 'to-purple-500', 
                    border: 'border-pink-500/30',
                    icon: 'bg-pink-500/20 border-pink-500/50 text-pink-400/60',
                    textColor: 'text-pink-300/60',
                    badge: 'linear-gradient(135deg, #F472B6 0%, #E879F9 50%, #A78BFA 100%)'
                  },
                  'V4': { 
                    from: 'from-teal-500', 
                    to: 'to-cyan-500', 
                    border: 'border-teal-500/30',
                    icon: 'bg-teal-500/20 border-teal-500/50 text-teal-400/60',
                    textColor: 'text-teal-300/60',
                    badge: 'linear-gradient(135deg, #2DD4BF 0%, #06B6D4 50%, #22D3EE 100%)'
                  }
                };

                const iconMap: { [key: string]: any } = {
                  'V1': MessageCircle,
                  'V2': Image,
                  'V3': BookOpen,
                  'V4': GraduationCap
                };

                const gradient = gradientMap[pkg.version] || gradientMap['V1'];
                const IconComponent = iconMap[pkg.version] || MessageCircle;
                const isComingSoon = pkg.version === 'V3' || pkg.version === 'V4';

                return (
                  <div key={pkg.id} className={`relative group w-full md:w-80 ${isComingSoon ? 'opacity-75' : ''}`}>
                    <div className={`absolute inset-0 bg-gradient-to-r ${gradient.from} ${gradient.to} rounded-2xl blur-lg ${isComingSoon ? 'opacity-50' : 'opacity-75 group-hover:opacity-100'} transition-opacity`}></div>
                    <div className={`relative ${isComingSoon ? 'bg-gray-900/60' : 'bg-gray-900/80'} backdrop-blur-md border ${gradient.border} rounded-2xl transition-all duration-300 overflow-hidden`}>
                      
                      {/* Cover Image Section */}
                      <div className="relative h-48 overflow-hidden rounded-t-2xl">
                        {pkg.coverImage && !isComingSoon ? (
                          <img 
                            src={pkg.coverImage} 
                            alt={pkg.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                            decoding="async"
                          />
                        ) : (
                          <div className={`w-full h-full bg-gradient-to-br ${gradient.from} ${gradient.to}`}></div>
                        )}
                        
                        {/* Icon - Top Left on Image */}
                        <div className={`absolute top-4 left-4 w-12 h-12 ${gradient.icon} border-2 rounded-full flex items-center justify-center z-10`}>
                          <IconComponent size={24} />
                        </div>
                        
                        {/* Version Badge - Top Right on Image */}
                        <div className="absolute top-4 right-4 z-10">
                          <span 
                            className="block text-5xl font-black leading-none tracking-wider"
                            style={{ 
                              letterSpacing: '0.1em',
                              background: gradient.badge,
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              backgroundClip: 'text',
                              textShadow: '0 0 8px rgba(96, 165, 250, 0.45), 0 0 18px rgba(96, 165, 250, 0.25)'
                            }}
                          >
                            {pkg.version}
                          </span>
                        </div>
                      </div>
                      
                      {/* Content Section */}
                      <div className="relative p-6 space-y-4">
                        <h3 className={`text-xl font-bold ${isComingSoon ? 'text-white/80' : 'text-white'}`}>{pkg.name}</h3>
                        <p className={`text-sm ${gradient.textColor} font-semibold`}>{pkg.description}</p>
                        
                        {/* Features List */}
                        {pkg.features && pkg.features.length > 0 && (
                          <ul className={`space-y-2 ${isComingSoon ? 'text-gray-400/60' : 'text-gray-300'} text-sm`}>
                            {pkg.features.map((feature, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <span className={`${gradient.textColor} mt-1`}>•</span>
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        
                        <div className="pt-4">
                          {isComingSoon ? (
                            <>
                              <p className="text-xl font-bold text-white/60 mb-4">เร็วๆ นี้</p>
                              <button 
                                disabled
                                className="w-full bg-gray-700/50 text-gray-400 py-3 rounded-xl font-semibold cursor-not-allowed"
                              >
                                Coming Soon
                              </button>
                            </>
                          ) : (
                            <>
                              <p className="text-3xl font-bold text-white mb-4">฿{pkg.price.toLocaleString()}</p>
                              <div className="flex gap-2">
                                <button 
                                  onClick={() => handlePackageSelect(pkg.version)}
                                  className={`flex-1 bg-gradient-to-r ${gradient.from} ${gradient.to} hover:from-blue-400 hover:to-purple-400 text-white py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105`}
                                >
                                  เลือกคอร์สนี้
                                </button>
                                <button 
                                  onClick={() => handleAddToCart({ 
                                    id: pkg.id, 
                                    name: pkg.name, 
                                    price: pkg.price, 
                                    version: pkg.version, 
                                    description: pkg.description 
                                  })}
                                  className={`bg-gradient-to-r ${gradient.from} ${gradient.to} hover:from-blue-400 hover:to-purple-400 text-white p-3 rounded-xl transition-all duration-300 transform hover:scale-105`}
                                  title="เพิ่มลงตระกร้า"
                                >
                                  <ShoppingCart size={20} />
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Statistics Section */}
      <section id="howto" className="py-20 px-4 bg-black/50">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div className="space-y-4">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto">
                <Users className="text-white" size={32} />
              </div>
              <p className="text-2xl font-bold">10,000+</p>
              <p className="text-gray-300">ผู้ใช้งานไว้วางใจ</p>
            </div>
            <div className="space-y-4">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto">
                <Star className="text-white" size={32} />
              </div>
              <p className="text-2xl font-bold">4.9/5</p>
              <p className="text-gray-300">คะแนนรีวิว</p>
            </div>
            <div className="space-y-4">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto">
                <Clock className="text-white" size={32} />
              </div>
              <p className="text-2xl font-bold">ประหยัดเวลา</p>
              <p className="text-gray-300">มากกว่า 80%</p>
            </div>
            <div className="space-y-4">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto">
                <Award className="text-white" size={32} />
              </div>
              <p className="text-2xl font-bold">ผลงานคุณภาพ</p>
              <p className="text-gray-300">ระดับมืออาชีพ</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="reviews" className="py-20 px-4 bg-black/40">
        <div className="container mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4">
            มั่นใจได้ด้วยรีวิวจากผู้ใช้งานจริง
          </h2>
          <div className="text-center mb-12">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="inline text-yellow-400" size={24} fill="currentColor" />
            ))}
          </div>

          <div className="relative overflow-hidden">
            <div className="flex transition-transform duration-1000 ease-in-out" id="testimonialCarousel">
              {/* Testimonial 1 */}
              <div className="w-full md:w-1/3 px-4 flex-shrink-0">
                <div className="bg-gray-800/50 rounded-lg p-6 space-y-4 h-full">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">SC</span>
                    </div>
                    <div>
                      <p className="font-semibold">S. Chai</p>
                      <p className="text-sm text-gray-400">เจ้าของธุรกิจออนไลน์</p>
                    </div>
                  </div>
                  <p className="text-gray-300">
                    "เครื่องมือ AI จากคอร์สนี้ช่วยประหยัดเวลาการทำงานได้หลายชั่วโมงต่อวัน ความเร็วในการสร้างคอนเทนต์เพิ่มขึ้น 3 เท่า!"
                  </p>
                </div>
              </div>

              {/* Testimonial 2 */}
              <div className="w-full md:w-1/3 px-4 flex-shrink-0">
                <div className="bg-gray-800/50 rounded-lg p-6 space-y-4 h-full">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">MN</span>
                    </div>
                    <div>
                      <p className="font-semibold">M. Nisa</p>
                      <p className="text-sm text-gray-400">คอนเทนต์ครีเอเตอร์</p>
                    </div>
                  </div>
                  <p className="text-gray-300">
                    "พร้อมต์ที่ได้จากคอร์สนี้เปลี่ยนชีวิต! ลูกค้าชอบผลงานมากขึ้น คุ้มค่าทุกบาทที่จ่ายไป"
                  </p>
                </div>
              </div>

              {/* Testimonial 3 */}
              <div className="w-full md:w-1/3 px-4 flex-shrink-0">
                <div className="bg-gray-800/50 rounded-lg p-6 space-y-4 h-full">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">WV</span>
                    </div>
                    <div>
                      <p className="font-semibold">W. Vichai</p>
                      <p className="text-sm text-gray-400">นักการตลาด</p>
                    </div>
                  </div>
                  <p className="text-gray-300">
                    "เหมาะสำหรับผู้เริ่มต้น! ไม่ต้องมีพื้นฐานด้าน AI ก็เรียนตามได้เลย แนะนำเลยครับ"
                  </p>
                </div>
              </div>

              {/* Testimonial 4 */}
              <div className="w-full md:w-1/3 px-4 flex-shrink-0">
                <div className="bg-gray-800/50 rounded-lg p-6 space-y-4 h-full">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">KP</span>
                    </div>
                    <div>
                      <p className="font-semibold">K. Ploy</p>
                      <p className="text-sm text-gray-400">ดีไซน์เนอร์</p>
                    </div>
                  </div>
                  <p className="text-gray-300">
                    "เทคนิคการออกแบบด้วย AI ช่วยให้สร้างภาพสวยงามได้เร็วกว่าเดิมมาก!"
                  </p>
                </div>
              </div>

              {/* Testimonial 5 */}
              <div className="w-full md:w-1/3 px-4 flex-shrink-0">
                <div className="bg-gray-800/50 rounded-lg p-6 space-y-4 h-full">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">TJ</span>
                    </div>
                    <div>
                      <p className="font-semibold">T. James</p>
                      <p className="text-sm text-gray-400">นักพัฒนาโปรแกรม</p>
                    </div>
                  </div>
                  <p className="text-gray-300">
                    "โครงสร้างคอร์สยอดเยี่ยม! AI Assistant ช่วยเพิ่มประสิทธิภาพการทำงานได้มาก"
                  </p>
                </div>
              </div>

              {/* Testimonial 6 */}
              <div className="w-full md:w-1/3 px-4 flex-shrink-0">
                <div className="bg-gray-800/50 rounded-lg p-6 space-y-4 h-full">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">LR</span>
                    </div>
                    <div>
                      <p className="font-semibold">L. Rose</p>
                      <p className="text-sm text-gray-400">นักศึกษา</p>
                    </div>
                  </div>
                  <p className="text-gray-300">
                    "เป็นนักศึกษา คอร์สนี้ช่วยในการทำการบ้านและวิจัยได้ดีมาก เครื่องมือ AI สุดยอด!"
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Payment Methods Section */}
      <section className="py-16 px-4 bg-black/60">
        <div className="container mx-auto">
          <div className="flex justify-center">
            <img 
              src="/coursedee-payment.jpg" 
              alt="Coursedee Payment Methods" 
              className="w-full max-w-4xl h-auto rounded-xl shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="py-12 px-4 bg-black/85 border-t border-purple-800/60">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Logo and Copyright */}
            <div className="space-y-4">
              <span className="text-xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                {hostname === 'edumi.co' || hostname === 'www.edumi.co' ? 'EduMi' : 'AIForYou'}
              </span>
              <p className="text-gray-400 text-sm">
                © 2024 {hostname === 'edumi.co' || hostname === 'www.edumi.co' ? 'EduMi' : 'AIForYou'} สงวนลิขสิทธิ์
              </p>
            </div>

            {/* Links */}
            <div className="space-y-4">
              <h3 className="font-semibold">เมนูลัด</h3>
              <div className="space-y-2">
                <a 
                  href={footerSettings.footerLinks.privacy}
                  className="block text-gray-400 hover:text-white text-sm transition-colors"
                >
                  นโยบายความเป็นส่วนตัว
                </a>
                <a 
                  href={footerSettings.footerLinks.terms}
                  className="block text-gray-400 hover:text-white text-sm transition-colors"
                >
                  เงื่อนไขการใช้งาน
                </a>
                <a 
                  href={footerSettings.footerLinks.contact}
                  className="block text-gray-400 hover:text-white text-sm transition-colors"
                >
                  ติดต่อเรา
                </a>
              </div>
            </div>

            {/* Social Media */}
            <div className="space-y-4">
              <h3 className="font-semibold">ติดตามเรา</h3>
              <div className="flex space-x-4">
                {footerSettings.social.facebook && (
                  <a 
                    href={footerSettings.social.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-500 transition-colors"
                  >
                    <Facebook size={20} />
                  </a>
                )}
                {footerSettings.social.youtube && (
                  <a 
                    href={footerSettings.social.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center hover:bg-red-500 transition-colors"
                  >
                    <Youtube size={20} />
                  </a>
                )}
                {footerSettings.social.tiktok && (
                  <a 
                    href={footerSettings.social.tiktok}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-black rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors border border-gray-600"
                  >
                    <Music size={20} />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </footer>
      </div>
    </div>
  );
};

export default CoursedeeLanding;
